import { useEffect, useRef, useState, useCallback } from 'react'
import JsBarcode from 'jsbarcode'

const BLUE   = '#1877F2'
const TEXT   = '#050505'
const MUTED  = '#65676B'
const BORDER = '#E4E6EB'
const BG     = '#F0F2F5'
const WHITE  = '#FFFFFF'

// ── Single barcode renderer ────────────────────────────────────────────────
export function BarcodeImage({ code, productName, shopName, width = 200, small = false }) {
  const svgRef = useRef(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!code || !svgRef.current) return
    try {
        JsBarcode(svgRef.current, code, {
          format: 'CODE128',
          width: small ? 1.2 : 1.6,
          height: small ? 36 : 52,
          displayValue: false,
          margin: 4,
          background: '#FFFFFF',
          lineColor: '#000000',
        })
        setError(false)
      } catch {
        setError(true)
      }
  }, [code, small])

  if (error) {
    return (
      <div style={{ padding: '8px', background: '#FEF2F2', borderRadius: '6px', fontSize: '11px', color: '#DC2626', textAlign: 'center' }}>
        Invalid barcode format
      </div>
    )
  }

  return (
    <div style={{ textAlign: 'center', background: WHITE, padding: small ? '6px 8px' : '10px 12px', borderRadius: '8px', border: '1px solid ' + BORDER, display: 'inline-block', minWidth: small ? '120px' : '160px' }}>
      <svg ref={svgRef} style={{ display: 'block', margin: '0 auto', maxWidth: '100%' }} />
      <div style={{ fontSize: small ? '9px' : '11px', fontWeight: 700, color: TEXT, fontFamily: 'JetBrains Mono, monospace', marginTop: '3px', letterSpacing: '0.05em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: small ? '120px' : '200px' }}>
        {code}
      </div>
      {productName && (
        <div style={{ fontSize: small ? '8px' : '10px', color: MUTED, marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: small ? '120px' : '200px' }}>
          {productName}
        </div>
      )}
      {shopName && (
        <div style={{ fontSize: small ? '7px' : '9px', color: '#B0B3B8', marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: small ? '120px' : '200px' }}>
          {shopName}
        </div>
      )}
    </div>
  )
}

// ── Print helpers ──────────────────────────────────────────────────────────
function generateBarcodeSVGString(code, opts = {}) {
  // Returns a data URL for an SVG barcode — used inside print HTML
  return new Promise((resolve) => {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    try {
        JsBarcode(svg, code, {
          format: 'CODE128',
          width: opts.width || 1.6,
          height: opts.height || 52,
          displayValue: false,
          margin: 4,
          background: '#FFFFFF',
          lineColor: '#000000',
        })
        const serialized = new XMLSerializer().serializeToString(svg)
        resolve('data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(serialized))))
    } catch {
      resolve('')
    }
  })
}

// ── Print single label ─────────────────────────────────────────────────────
export async function printSingleLabel({ code, productName, shopName, format = '62x29' }) {
  const svgUrl = await generateBarcodeSVGString(code, { width: 1.6, height: 52 })

  // Dimensions in mm
  const dims = {
    '62x29': { w: 62, h: 29 },
    '58mm':  { w: 50, h: 25 },
    '80mm':  { w: 72, h: 30 },
  }
  const d = dims[format] || dims['62x29']

  const html = `<!DOCTYPE html>
<html>
<head>
<style>
  @page { size: ${d.w}mm ${d.h}mm; margin: 0; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { width: ${d.w}mm; height: ${d.h}mm; display: flex; align-items: center; justify-content: center; font-family: 'Space Grotesk', Arial, sans-serif; background: white; }
  .label { width: 100%; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 2mm; }
  .barcode { max-width: 90%; height: auto; display: block; margin: 0 auto; }
  .code { font-family: 'Courier New', monospace; font-size: 7pt; font-weight: 700; text-align: center; letter-spacing: 0.04em; margin-top: 1mm; }
  .name { font-size: 6pt; text-align: center; color: #444; margin-top: 0.5mm; overflow: hidden; white-space: nowrap; max-width: 90%; text-overflow: ellipsis; }
  .shop { font-size: 5pt; text-align: center; color: #888; margin-top: 0.5mm; }
</style>
</head>
<body>
<div class="label">
  ${svgUrl ? `<img class="barcode" src="${svgUrl}" />` : ''}
  <div class="code">${code}</div>
  ${productName ? `<div class="name">${productName}</div>` : ''}
  ${shopName ? `<div class="shop">${shopName}</div>` : ''}
</div>
</body>
</html>`

  const win = window.open('', '_blank', 'width=400,height=300')
  if (!win) { alert('Please allow popups to print labels'); return }
  win.document.write(html)
  win.document.close()
  win.onload = () => { win.focus(); win.print(); win.close() }
}

// ── Print A4 sheet (24 labels) ─────────────────────────────────────────────
export async function printA4Sheet(items, shopName) {
  // items = [{ code, productName }] — up to 24
  const labelItems = items.slice(0, 24)

  // Generate all SVG urls in parallel
  const svgUrls = await Promise.all(
    labelItems.map(item => generateBarcodeSVGString(item.code, { width: 1.4, height: 40 }))
  )

  const labelsHtml = labelItems.map((item, i) => `
    <div class="label">
      ${svgUrls[i] ? `<img class="barcode" src="${svgUrls[i]}" />` : ''}
      <div class="code">${item.code}</div>
      ${item.productName ? `<div class="name">${item.productName}</div>` : ''}
      ${shopName ? `<div class="shop">${shopName}</div>` : ''}
    </div>
  `).join('')

  const html = `<!DOCTYPE html>
<html>
<head>
<style>
  @page { size: A4; margin: 10mm; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: Arial, sans-serif; background: white; }
  .grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 3mm; width: 100%; }
  .label { border: 0.5px solid #ccc; border-radius: 2mm; padding: 2mm; display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 28mm; page-break-inside: avoid; }
  .barcode { max-width: 95%; height: auto; display: block; margin: 0 auto; }
  .code { font-family: 'Courier New', monospace; font-size: 6.5pt; font-weight: 700; text-align: center; letter-spacing: 0.03em; margin-top: 1mm; }
  .name { font-size: 6pt; text-align: center; color: #444; margin-top: 0.5mm; overflow: hidden; white-space: nowrap; max-width: 100%; text-overflow: ellipsis; }
  .shop { font-size: 5pt; text-align: center; color: #888; margin-top: 0.3mm; }
</style>
</head>
<body>
<div class="grid">
${labelsHtml}
</div>
</body>
</html>`

  const win = window.open('', '_blank', 'width=800,height=600')
  if (!win) { alert('Please allow popups to print labels'); return }
  win.document.write(html)
  win.document.close()
  win.onload = () => { win.focus(); win.print(); win.close() }
}

// ── Barcode Label Modal ────────────────────────────────────────────────────
// product: { product_code, name }
// variants: [{ id, variant_name, variant_code, stock }]
// shopName: string
export function BarcodeLabelModal({ product, variants, shopName, onClose }) {
  const [format, setFormat]         = useState('62x29')
  const [printing, setPrinting]     = useState(false)
  const [selectedVariant, setSelectedVariant] = useState(null)
  const [qty, setQty]               = useState(1)

  // Determine what codes we're working with
  const hasVariants = variants && variants.length > 0
  const activeCode  = hasVariants
    ? (selectedVariant ? (selectedVariant.variant_code || selectedVariant.variant_name) : null)
    : product.product_code

  const activeLabel = hasVariants
    ? (selectedVariant ? product.name + ' · ' + selectedVariant.variant_name : '')
    : product.name

  const handlePrintSingle = useCallback(async () => {
    if (!activeCode) return
    setPrinting(true)
    // Print qty copies
    for (let i = 0; i < Number(qty || 1); i++) {
      await printSingleLabel({ code: activeCode, productName: activeLabel, shopName, format })
    }
    setPrinting(false)
  }, [activeCode, activeLabel, shopName, format, qty])

  const handlePrintA4 = useCallback(async () => {
    setPrinting(true)
    let items = []
    if (hasVariants) {
      // All variants on sheet
      variants.forEach(v => {
        const code = v.variant_code || v.variant_name
        for (let i = 0; i < Number(qty || 1); i++) {
          items.push({ code, productName: product.name + ' · ' + v.variant_name })
        }
      })
    } else {
      for (let i = 0; i < Math.min(Number(qty || 1), 24); i++) {
        items.push({ code: product.product_code, productName: product.name })
      }
    }
    await printA4Sheet(items.slice(0, 24), shopName)
    setPrinting(false)
  }, [hasVariants, variants, product, shopName, qty])

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 60 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: WHITE, borderRadius: '20px 20px 0 0', width: '100%', maxWidth: '540px', maxHeight: '92vh', overflow: 'auto', fontFamily: 'Space Grotesk, sans-serif' }}>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid ' + BORDER, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, background: WHITE, zIndex: 1 }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: TEXT }}>Print barcode label</div>
            <div style={{ fontSize: '11.5px', color: MUTED, marginTop: '2px' }}>{product.name}</div>
          </div>
          <button onClick={onClose}
            style={{ width: '28px', height: '28px', borderRadius: '50%', background: BG, border: 'none', fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: MUTED }}>
            ×
          </button>
        </div>

        <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* Variant selector */}
          {hasVariants && (
            <div>
              <div style={{ fontSize: '11.5px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>
                Select variant to preview
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                <button onClick={() => setSelectedVariant(null)}
                  style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid', borderColor: !selectedVariant ? BLUE : BORDER, background: !selectedVariant ? '#E7F3FF' : WHITE, fontSize: '12px', fontWeight: 600, color: !selectedVariant ? BLUE : TEXT, cursor: 'pointer' }}>
                  All variants
                </button>
                {variants.map(v => (
                  <button key={v.id} onClick={() => setSelectedVariant(v)}
                    style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid', borderColor: selectedVariant?.id === v.id ? BLUE : BORDER, background: selectedVariant?.id === v.id ? '#E7F3FF' : WHITE, fontSize: '12px', fontWeight: 600, color: selectedVariant?.id === v.id ? BLUE : TEXT, cursor: 'pointer' }}>
                    {v.variant_name}
                    {v.variant_code && <span style={{ fontSize: '10px', color: MUTED, marginLeft: '5px', fontFamily: 'JetBrains Mono, monospace' }}>{v.variant_code}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Preview */}
          <div>
            <div style={{ fontSize: '11.5px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>
              Preview
            </div>
            {activeCode ? (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <BarcodeImage code={activeCode} productName={activeLabel} shopName={shopName} />
              </div>
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', background: BG, borderRadius: '10px', fontSize: '12px', color: MUTED }}>
                {hasVariants ? 'Select a variant above to preview its barcode' : 'No product code set'}
              </div>
            )}
          </div>

          {/* All variants preview */}
          {hasVariants && !selectedVariant && variants.length > 0 && (
            <div>
              <div style={{ fontSize: '11.5px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>
                All variant barcodes
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
                {variants.map(v => {
                  const code = v.variant_code || v.variant_name
                  return (
                    <BarcodeImage key={v.id} code={code} productName={v.variant_name} shopName={shopName} small />
                  )
                })}
              </div>
            </div>
          )}

          {/* Label format */}
          <div>
            <div style={{ fontSize: '11.5px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>
              Label format
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {[
                { key: '62x29', label: 'Standard label', desc: '62mm × 29mm' },
                { key: '58mm',  label: 'Thermal 58mm',   desc: '58mm roll' },
                { key: '80mm',  label: 'Thermal 80mm',   desc: '80mm roll' },
              ].map(f => (
                <button key={f.key} onClick={() => setFormat(f.key)}
                  style={{ padding: '10px 12px', borderRadius: '10px', border: '1px solid', borderColor: format === f.key ? BLUE : BORDER, background: format === f.key ? '#E7F3FF' : WHITE, textAlign: 'left', cursor: 'pointer' }}>
                  <div style={{ fontSize: '12.5px', fontWeight: 600, color: format === f.key ? BLUE : TEXT }}>{f.label}</div>
                  <div style={{ fontSize: '11px', color: MUTED, marginTop: '2px' }}>{f.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Quantity */}
          <div>
            <div style={{ fontSize: '11.5px', fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>
              {hasVariants && !selectedVariant ? 'Copies per variant' : 'Number of copies'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button onClick={() => setQty(q => Math.max(1, Number(q) - 1))}
                style={{ width: '32px', height: '32px', borderRadius: '8px', border: '1px solid ' + BORDER, background: WHITE, fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: TEXT }}>
                −
              </button>
              <input type="number" value={qty} onChange={e => setQty(Math.max(1, Math.min(24, Number(e.target.value))))}
                min={1} max={24}
                style={{ width: '60px', textAlign: 'center', border: '1px solid ' + BORDER, borderRadius: '8px', padding: '7px', fontSize: '14px', fontWeight: 700, outline: 'none', fontFamily: 'Space Grotesk, sans-serif' }}
                onFocus={e => e.target.style.borderColor = BLUE}
                onBlur={e => e.target.style.borderColor = BORDER} />
              <button onClick={() => setQty(q => Math.min(24, Number(q) + 1))}
                style={{ width: '32px', height: '32px', borderRadius: '8px', border: '1px solid ' + BORDER, background: WHITE, fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: TEXT }}>
                +
              </button>
              <span style={{ fontSize: '11.5px', color: MUTED }}>max 24 per sheet</span>
            </div>
          </div>

          {/* Print buttons */}
          <div style={{ display: 'flex', gap: '10px', paddingBottom: '8px' }}>
            <button onClick={handlePrintSingle} disabled={printing || !activeCode}
              style={{ flex: 1, padding: '13px', borderRadius: '10px', border: '1px solid ' + BORDER, background: printing || !activeCode ? BG : WHITE, fontSize: '13px', fontWeight: 600, color: printing || !activeCode ? MUTED : TEXT, cursor: printing || !activeCode ? 'not-allowed' : 'pointer' }}>
              🖨 Print label{qty > 1 ? 's (' + qty + ')' : ''}
            </button>
            <button onClick={handlePrintA4} disabled={printing}
              style={{ flex: 1, padding: '13px', borderRadius: '10px', background: printing ? BORDER : BLUE, border: 'none', fontSize: '13px', fontWeight: 600, color: WHITE, cursor: printing ? 'not-allowed' : 'pointer' }}>
              📄 {hasVariants && !selectedVariant ? 'Print all on A4' : 'Print A4 sheet'}
            </button>
          </div>

          <div style={{ fontSize: '11.5px', color: MUTED, textAlign: 'center', paddingBottom: '4px', lineHeight: 1.6 }}>
            A browser print dialog will open. Select your label printer or save as PDF.
            For thermal printers — select the correct paper size in the print dialog.
          </div>
        </div>
      </div>
    </div>
  )
}

export default BarcodeLabelModal